import { User } from "oidc-client";

/** Grace period, in milliseconds, to refresh the token before it expires. */
const EXPIRY_GRACE_ms = 60e3;


class AuthService {
  /** Currently logged in user. */
  private currentUser: User | undefined = undefined;
  /** Timeout handler for background token refresh. */
  private autoRefreshTimer: NodeJS.Timeout | null = null;

  /** Event listeners, principally refresh token. */
  private eventListeners: { [name: string]: (() => void)[] } = {
    refresh: [],
  };

  /** Add a new event listener. */
  public addEventListener(name: string, handler: () => void) {
    this.removeEventListener(name, handler);
    this.eventListeners[name]?.push(handler);
  }

  /** Remove an event listener, if it has been registered. */
  public removeEventListener(name: string, handler: () => void) {
    if (this.eventListeners[name]) {
      this.eventListeners[name] = this.eventListeners[name].filter(
        (h) => h !== handler
      );
    }
  }

  /** Fire all listeners for an event. */
  private fireEventListeners(name: string) {
    this.eventListeners[name]?.forEach((handler) => handler());
  }

  /**
   * The currently authenticated user, or *undefined* if the user is not
   * authenticated.
   */
  public get user(): User | undefined {
    return this.currentUser;
  }

  /**
   * The current access token, or *undefined* if the user is not authenticated.
   */
  public get accessToken(): string | undefined {
    return this.currentUser?.access_token;
  }

  /**
   * The current user's userkey, or *0* if the user is not authenticated.
   */
  public get userkey(): number {
    return +(this.currentUser?.profile.sub || 0);
  }

  /**
   * Returns a Headers object including bearer authorization and custom X-Tenant
   * and X-Scheme when using host-issued credentials.
   * @param mimeType Optional MIME type, default is application/json.
   */
  public getHeaders(mimeType?: string): Headers {
    const headers = new Headers({
      authorization: `Bearer ${this.accessToken}`,
      "Content-Type": mimeType || "application/json",
    });
    return headers;
  }

  /**
   * Logs the user in by creating a login form and posting it to the backend
   * server, which authenticates to the Identity Server.
   */
  public async login(): Promise<void> {
    const form = document.createElement("form");
    form.action = `/auth/login`;
    form.method = "GET";

    /** Add a parameter to the form to be submitted. */
    const addParam = (name: string, value: string) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      form.appendChild(input);
    };

    // Track landing page.
    addParam(
      "target",
      `${document.location.pathname}${document.location.search}`
    );

    // Submit the form.
    document.body.appendChild(form);
    form.submit();
  }

  /**
   * Logs the user out via Passport and Identity Server.
   */
  public async logout(): Promise<void> {
    document.location.replace(
      `/auth/logout?id_token=${this.currentUser?.id_token}`
    );
  }

  /**
   * Returns the current user, loading it via the backend server refresh token
   * if required.
   */
  public async getUser(): Promise<User | undefined> {
    if (this.currentUser) {
      return this.currentUser;
    }
    return this.refreshToken();
  }

  /**
   * Set a timer to refresh the token before it expires.
   * @param exp Token expiry time, in epoch seconds.
   */
  private autoRefresh(exp: number) {
    if (this.autoRefreshTimer) {
      clearTimeout(this.autoRefreshTimer);
    }
    const expiresIn = exp * 1000 - new Date().valueOf();
    console.log(`Token expires in ${Math.floor(expiresIn / 1e3)} s`);
    this.autoRefreshTimer = setTimeout(
      () => this.refreshToken(),
      expiresIn - EXPIRY_GRACE_ms
    );
  }

  /**
   * Loads the refresh token and user information from the backend server.
   */
  private async refreshToken() {
    const res = await fetch("/auth/refresh");
    if (res.status === 200 || res.status === 201) {
      const user = (await res.json()) as User;
      this.currentUser = user;
      this.autoRefresh(user.profile.exp);
      this.fireEventListeners("refresh");
    } else {
      this.currentUser = undefined;
      const err = await res.text();
      console.error(`Failed to get refresh token: ${err}`);
    }
    return this.currentUser;
  }
}

const authService = new AuthService();
export default authService;
